import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import { documentService } from '../../services/apiService';
import { useToast } from '../../components/ui/Toast';

const DOCUMENT_TYPE_LABELS = {
  GST_CERTIFICATE: 'GST Certificate',
  PAN_CARD: 'PAN Card',
  BANK_PROOF: 'Bank Proof Document',
  MSME_CERTIFICATE: 'MSME Certificate',
  MSME_DECLARATION: 'MSME Declaration',
  DIGITAL_SIGNATURE: 'Digital Signature',
  INCORPORATION_CERTIFICATE: 'Incorporation Certificate',
  OTHER: 'Other Document',
};

const formatDocumentType = (type) => {
  return DOCUMENT_TYPE_LABELS[type] || type;
};

const PublicDocumentReplacement = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const toast = useToast();
  
  const token = searchParams.get('token');
  
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [replacementData, setReplacementData] = useState(null);
  const [selectedFiles, setSelectedFiles] = useState({}); // { document_id: File }
  
  useEffect(() => {
    if (!token) {
      setError('Invalid or missing token. Please use the link from your email.');
      setLoading(false);
      return;
    }
    
    fetchReplacementInfo();
  }, [token]);
  
  const fetchReplacementInfo = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await documentService.getReplacementInfo(token);
      setReplacementData(response.data);
    } catch (err) {
      console.error('Error fetching replacement info:', err);
      setError(err.response?.data?.message || 'Failed to load replacement information. The link may be expired or invalid.');
    } finally {
      setLoading(false);
    }
  };
  
  const handleFileSelect = (documentId, file) => {
    if (!file) return;
    
    // Validate file type
    const allowedTypes = [
      'application/pdf',
      'image/jpeg',
      'image/jpg',
      'image/png',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    ];
    
    if (!allowedTypes.includes(file.type)) {
      toast.error('Invalid file type. Please upload PDF, JPG, PNG, or DOC files.');
      return;
    }
    
    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File size exceeds 10MB limit.');
      return;
    }
    
    setSelectedFiles(prev => ({
      ...prev,
      [documentId]: file,
    }));
  };
  
  const handleSubmit = async () => {
    if (!replacementData) return;
    
    // Check if all rejected documents have replacement files
    const rejectedDocIds = replacementData.rejected_documents.map(doc => doc.id);
    const missingFiles = rejectedDocIds.filter(id => !selectedFiles[id]);
    
    if (missingFiles.length > 0) {
      toast.warning(`Please upload replacement files for all ${missingFiles.length} rejected document(s).`);
      return;
    }
    
    try {
      setSubmitting(true);
      
      // Prepare replacements array
      const replacements = rejectedDocIds.map(docId => ({
        document_id: docId,
      }));
      
      // Prepare files array in the same order
      const files = rejectedDocIds.map(docId => selectedFiles[docId]);
      
      await documentService.submitReplacements(token, replacements, files);
      
      toast.success('Documents replaced successfully! Your application will be reviewed again.');
      
      // Clear form
      setSelectedFiles({});
      
      // Show success message and redirect after a delay
      setTimeout(() => {
        navigate('/');
      }, 3000);
    } catch (err) {
      console.error('Error submitting replacements:', err);
      toast.error(err.response?.data?.message || 'Failed to submit replacements. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };
  
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <Icon name="Loader" size={48} className="animate-spin mx-auto mb-4 text-primary" />
          <p className="text-slate-600">Loading replacement information...</p>
        </div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg border border-red-200 shadow-lg p-8 max-w-md w-full">
          <div className="text-center">
            <Icon name="XCircle" size={48} className="text-red-600 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-slate-900 mb-2">Error</h2>
            <p className="text-slate-600 mb-6">{error}</p>
            <Button
              variant="outline"
              onClick={() => navigate('/')}
            >
              Go to Home
            </Button>
          </div>
        </div>
      </div>
    );
  }
  
  if (!replacementData) {
    return null;
  }
  
  const { vendor, rejected_documents, all_documents, rejection_reason, expires_at } = replacementData;
  const rejectedDocIds = rejected_documents.map(doc => doc.id);
  const hasAllReplacements = rejectedDocIds.every(id => selectedFiles[id]);
  
  return (
    <div className="min-h-screen bg-slate-50 py-8 px-4"  style={{
      height: "100%",
      overflowY: "auto"
    }}>
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h1 className="text-2xl font-bold text-slate-900">Document Replacement</h1>
            <div className="text-sm text-slate-600">
              <Icon name="Calendar" size={16} className="inline mr-1" />
              Expires: {new Date(expires_at).toLocaleDateString()}
            </div>
          </div>
          
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h2 className="font-semibold text-blue-900 mb-2">Vendor Information</h2>
            <p className="text-blue-800"><strong>Company:</strong> {vendor.company_name}</p>
            <p className="text-blue-800"><strong>Contact:</strong> {vendor.contact_person_name}</p>
            <p className="text-blue-800"><strong>Email:</strong> {vendor.email}</p>
          </div>
          
          {rejection_reason && (
            <div className="mt-4 bg-red-50 border border-red-200 rounded-lg p-4">
              <h3 className="font-semibold text-red-900 mb-2">Rejection Reason</h3>
              <p className="text-red-800">{rejection_reason}</p>
            </div>
          )}
        </div>
        
        {/* Documents List */}
        <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6 mb-6">
          <h2 className="text-xl font-bold text-slate-900 mb-4">All Documents</h2>
          
          <div className="space-y-4">
            {all_documents.map((doc) => {
              const isRejected = doc.is_rejected;
              const isVerified = doc.status === 'VERIFIED';
              const isPending = doc.status === 'PENDING';
              const selectedFile = selectedFiles[doc.id];
              
              return (
                <div
                  key={doc.id}
                  className={`border rounded-lg p-4 ${
                    isRejected
                      ? 'border-red-300 bg-red-50'
                      : isVerified
                      ? 'border-green-300 bg-green-50'
                      : 'border-slate-200 bg-slate-50'
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <Icon
                          name={isRejected ? 'XCircle' : isVerified ? 'CheckCircle' : 'FileText'}
                          size={20}
                          className={
                            isRejected
                              ? 'text-red-600'
                              : isVerified
                              ? 'text-green-600'
                              : 'text-slate-600'
                          }
                        />
                        <div>
                          <h3 className="font-medium text-slate-900">
                            {formatDocumentType(doc.document_type)}
                          </h3>
                          <p className="text-sm text-slate-600">{doc.file_name}</p>
                        </div>
                        <span
                          className={`px-2 py-1 rounded text-xs font-medium ${
                            isRejected
                              ? 'bg-red-100 text-red-800'
                              : isVerified
                              ? 'bg-green-100 text-green-800'
                              : 'bg-yellow-100 text-yellow-800'
                          }`}
                        >
                          {isRejected ? 'REJECTED' : doc.status}
                        </span>
                      </div>
                      
                      {isRejected && (
                        <div className="mt-3">
                          <label className="block text-sm font-medium text-slate-700 mb-2">
                            Upload Replacement File
                          </label>
                          <div className="flex items-center gap-3">
                            <label className="flex-1">
                              <input
                                type="file"
                                accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                                onChange={(e) => handleFileSelect(doc.id, e.target.files[0])}
                                className="hidden"
                                disabled={submitting}
                              />
                              <div className="border-2 border-dashed border-slate-300 rounded-lg p-4 cursor-pointer hover:border-primary transition-colors">
                                <div className="text-center">
                                  <Icon name="Upload" size={24} className="mx-auto mb-2 text-slate-400" />
                                  <p className="text-sm text-slate-600">
                                    {selectedFile ? (
                                      <span className="text-primary font-medium">{selectedFile.name}</span>
                                    ) : (
                                      'Click to select file'
                                    )}
                                  </p>
                                  <p className="text-xs text-slate-500 mt-1">
                                    PDF, JPG, PNG, DOC (Max 10MB)
                                  </p>
                                </div>
                              </div>
                            </label>
                            {selectedFile && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                  const newFiles = { ...selectedFiles };
                                  delete newFiles[doc.id];
                                  setSelectedFiles(newFiles);
                                }}
                                disabled={submitting}
                              >
                                <Icon name="X" size={14} />
                              </Button>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        
        {/* Submit Button */}
        <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-600">
                {rejectedDocIds.length} document(s) need replacement
              </p>
              {!hasAllReplacements && (
                <p className="text-sm text-red-600 mt-1">
                  Please upload replacement files for all rejected documents
                </p>
              )}
            </div>
            <Button
              variant="default"
              onClick={handleSubmit}
              disabled={submitting || !hasAllReplacements}
              loading={submitting}
            >
              <Icon name="Check" size={16} className="mr-2" />
              Submit Replacements
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PublicDocumentReplacement;
